/**
 * React Query hooks for MCP Template Catalog operations.
 *
 * Tenant-scoped: browsing the catalog (preview + published).
 * System-scoped: admin CRUD + lifecycle management.
 *
 * Sprint: 40, Task: S40-B02
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  MCPTemplateCatalogResponse,
  MCPTemplateSummary,
  MCPTemplateResponse,
  TemplateCategory,
  TemplateLifecycleStatus,
} from '../template-types';

// =============================================================================
// Query Keys
// =============================================================================

export const templateKeys = {
  all: ['mcp-templates'] as const,
  // Tenant catalog keys
  catalog: (tenantId: string) => [...templateKeys.all, 'catalog', tenantId] as const,
  catalogFiltered: (tenantId: string, category?: TemplateCategory) =>
    [...templateKeys.catalog(tenantId), { category }] as const,
  detail: (tenantId: string, slug: string) =>
    [...templateKeys.all, 'detail', tenantId, slug] as const,
  // System admin keys
  system: () => [...templateKeys.all, 'system'] as const,
  systemDetail: (slug: string) => [...templateKeys.all, 'system', slug] as const,
  // Documentation
  docs: (slug: string) => [...templateKeys.all, 'docs', slug] as const,
};

// =============================================================================
// Tenant-scoped hooks (catalog browsing)
// =============================================================================

interface UseMCPCatalogOptions {
  category?: TemplateCategory;
  offset?: number;
  limit?: number;
}

/**
 * List templates from the catalog (preview + published only).
 */
export function useMCPCatalog(options: UseMCPCatalogOptions = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const { category, offset = 0, limit = 50 } = options;

  return useQuery({
    queryKey: templateKeys.catalogFiltered(tenantId!, category),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (offset) params.set('offset', String(offset));
      params.set('limit', String(limit));

      const queryStr = params.toString();
      const { data } = await apiClient.get<MCPTemplateCatalogResponse>(
        `/api/v1/mcp/templates${queryStr ? `?${queryStr}` : ''}`,
      );
      return data;
    },
    enabled: !!tenantId,
  });
}

/**
 * Get a single template by slug (catalog view — preview + published).
 */
export function useMCPCatalogTemplate(slug: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: templateKeys.detail(tenantId!, slug),
    queryFn: async () => {
      const { data } = await apiClient.get<MCPTemplateResponse>(`/api/v1/mcp/templates/${slug}`);
      return data;
    },
    enabled: !!tenantId && !!slug,
  });
}

// =============================================================================
// System admin hooks (no tenant context — admin/* routes)
// =============================================================================

interface SystemTemplatesListResponse {
  templates: MCPTemplateSummary[];
  total: number;
}

/**
 * List ALL templates (system admin — all statuses).
 */
export function useSystemTemplates() {
  return useQuery({
    queryKey: templateKeys.system(),
    queryFn: async () => {
      const response = await apiClient.get<{ data: SystemTemplatesListResponse }>(
        '/api/v1/admin/mcp-templates'
      );
      return response.data.data ?? response.data;
    },
  });
}

/**
 * Get a single template by slug (system admin).
 */
export function useSystemTemplate(slug: string) {
  return useQuery({
    queryKey: templateKeys.systemDetail(slug),
    queryFn: async () => {
      const response = await apiClient.get<{ data: MCPTemplateResponse }>(
        `/api/v1/admin/mcp-templates/${slug}`
      );
      return response.data.data ?? response.data;
    },
    enabled: !!slug,
  });
}

/**
 * Update a template lifecycle status or other fields (system admin).
 */
export function useUpdateSystemTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, data: mutationData }: {
      slug: string;
      data: {
        lifecycle_status?: TemplateLifecycleStatus;
        display_name?: string;
        description?: string;
        is_active?: boolean;
        is_beta?: boolean;
        name?: string;
        icon?: string;
        category?: string;
        config_schema?: Record<string, unknown>;
        secrets_schema?: Record<string, unknown>;
        base_policies?: Record<string, unknown>;
        default_tools?: Array<Record<string, unknown>>;
        tool_discovery_mode?: string;
        documentation_url?: string;
        setup_instructions?: string;
      };
    }) => {
      const response = await apiClient.patch<{ data: MCPTemplateResponse }>(
        `/api/v1/admin/mcp-templates/${slug}`,
        mutationData
      );
      return response.data.data ?? response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Create a new template (system admin).
 */
export function useCreateSystemTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mutationData: {
      name: string;
      slug: string;
      display_name: string;
      description?: string;
      icon?: string;
      category: string;
      config_schema: Record<string, unknown>;
      secrets_schema?: Record<string, unknown>;
      base_policies?: Record<string, unknown>;
      default_tools?: Array<Record<string, unknown>>;
      tool_discovery_mode?: string;
      documentation_url?: string;
      setup_instructions?: string;
      is_beta?: boolean;
    }) => {
      const response = await apiClient.post<{ data: MCPTemplateResponse }>(
        '/api/v1/admin/mcp-templates',
        mutationData
      );
      return response.data.data ?? response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Delete a template (system admin — soft delete).
 */
export function useDeleteSystemTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      await apiClient.delete(`/api/v1/admin/mcp-templates/${slug}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

// =============================================================================
// Icon upload / delete
// =============================================================================

/**
 * Upload a custom icon image for a template (system admin).
 */
export function useUploadTemplateIcon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, file }: { slug: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.put<{ data: { slug: string; has_custom_icon: boolean } }>(
        `/api/v1/admin/mcp-templates/${slug}/icon`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.data ?? response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Delete the custom icon for a template (system admin).
 */
export function useDeleteTemplateIcon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      await apiClient.delete(`/api/v1/admin/mcp-templates/${slug}/icon`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

// =============================================================================
// Documentation
// =============================================================================

/**
 * Fetch template documentation (README.md) as raw Markdown.
 */
export function useTemplateDocumentation(slug: string) {
  return useQuery({
    queryKey: templateKeys.docs(slug),
    queryFn: async () => {
      const response = await apiClient.get<string>(`/api/v1/mcp-templates/${slug}/docs`, {
        headers: { Accept: 'text/markdown' },
        transformResponse: [(respData: string) => respData],
      });
      return response.data;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 min cache
    retry: false,
  });
}
